/**
 * ScanCapturePage — mobile-first camera capture page for shop scanning.
 *
 * Flow:
 *   1. Enter shop/section name
 *   2. Start camera preview (GPS sampling starts automatically)
 *   3. Record (auto-capture at 2fps)
 *   4. Confirm location (multi-sample average)
 *   5. Review frames
 *   6. Upload → track progress → done
 */

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCamera } from '@hooks/useCamera'
import { useUploader } from '@hooks/useUploader'
import { useLocation } from '@hooks/useLocation'
import { useDeviceMotion } from '@hooks/useDeviceMotion'
import { useShopStore } from '../../stores/shopStore'
import { useAuthStore } from '../../stores/authStore'
import {
  getPendingSessions,
  createSession,
  deleteSessionAndFrames,
  updateSession,
  generateSessionId,
  getStorageEstimate,
  getFramesBySession,
} from '@services/frameStore'
import type { ScanSession, PersistedFrame } from '@services/frameStore'
import { getScanSettings, setScanSettings } from '@utils/scanSettings'
import { apiUrl } from '../../config'
import type { ResolutionTier } from '@utils/scanSettings'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import './ScanCapturePage.css'

type PageStep = 'resume-prompt' | 'setup' | 'camera' | 'confirm-location' | 'edit-location' | 'review' | 'upload'

export default function ScanCapturePage() {
  const { t } = useTranslation('scan')
  const [step, setStep] = useState<PageStep>('setup')
  const navigate = useNavigate()

  // Reactive URL params — useSearchParams updates when navigate() is called
  const [searchParams] = useSearchParams()

  // Read shop/section from URL params (set by ShopsPage "Scan" button)
  const [shopId, setShopId] = useState(searchParams.get('shopId') || '')
  const [sectionName, setSectionName] = useState(searchParams.get('sectionName') || '')
  const [sectionIdFromUrl, setSectionIdFromUrl] = useState(searchParams.get('sectionId') || '')
  const [newSectionName, setNewSectionName] = useState('')

  // Rescan mode: triggered from viewer context menu or NavigationMenu
  const rescanMode = searchParams.get('mode') === 'rescan'
  const anchorFrameIndex = searchParams.get('anchorFrameIndex')
    ? parseInt(searchParams.get('anchorFrameIndex')!, 10)
    : null
  const anchorFrameFile = searchParams.get('anchorFrameFile') || null

  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  // User's shops from store
  const token = useAuthStore((s) => s.token)
  const { shops, loading: shopsLoading, fetchShops, createShop, updateShop, deleteShop, deleteSection } = useShopStore()
  const isNewShop = shopId === '__new_shop__'
  const selectedShop = isNewShop ? null : (shops.find((s) => s.id === shopId) || null)
  const [newShopName, setNewShopName] = useState('')

  // Details tab form state
  const [detailsName, setDetailsName] = useState('')
  const [detailsVisibility, setDetailsVisibility] = useState<'public' | 'private'>('public')
  const [detailsCategory, setDetailsCategory] = useState('Shop')
  const [detailsCustomCategory, setDetailsCustomCategory] = useState('')
  const [detailsShopType, setDetailsShopType] = useState('Supermarket')
  const [sectionCategory, setSectionCategory] = useState('')
  const [detailsDescription, setDetailsDescription] = useState('')
  const [detailsPhone, setDetailsPhone] = useState('')
  const [detailsWhatsapp, setDetailsWhatsapp] = useState('')
  const [detailsSameWhatsapp, setDetailsSameWhatsapp] = useState(false)
  const [detailsOpeningHoursEnabled, setDetailsOpeningHoursEnabled] = useState(false)
  const [detailsOpenHour, setDetailsOpenHour] = useState('09:00')
  const [detailsCloseHour, setDetailsCloseHour] = useState('21:00')
  const [detailsOpenDays, setDetailsOpenDays] = useState<number[]>([0, 1, 2, 3, 4])
  const [accordionOpen, setAccordionOpen] = useState<Record<string, boolean>>({ basicInfo: true })
  const [, setDetailsSaved] = useState(false)

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function autoScanName() {
  const now = new Date()
  return `Scan ${MONTHS[now.getMonth()]} ${now.getDate()}`
}

const SCAN_CATEGORIES = [
  'Shop', 'Vehicle', 'Product', 'Animal', 'House / Room', 'Person',
  'Furniture', 'Nature', 'Food', 'Art / Object', 'Other',
]

const SHOP_TYPES = [
  'Supermarket', 'Clothing', 'Restaurant', 'Cafe', 'Electronics',
  'Pharmacy', 'Bookstore', 'Bakery', 'Jewelry', 'Beauty / Salon',
  'Hardware', 'Sports', 'Furniture Store', 'Toy Store', 'General Shop',
]

const SECTION_CATEGORIES_BY_SHOP_TYPE: Record<string, string[]> = {
  Supermarket:       ['Entrance','Produce','Dairy','Bakery','Meat','Frozen','Beverages','Checkout','Aisle','Storage'],
  Clothing:          ['Storefront',"Men's","Women's",'Kids','Accessories','Fitting Room','Checkout','Display','Sale Area','Window'],
  Restaurant:        ['Entrance','Dining Area','Bar','Kitchen','Terrace','Restrooms','Takeaway Counter','Display','Storage'],
  Cafe:              ['Entrance','Counter','Seating','Terrace','Display Case','Bar','Kitchen','Restrooms'],
  Electronics:       ['Storefront','Phones','Computers','Accessories','Gaming','Appliances','Display','Service Desk','Checkout'],
  Pharmacy:          ['Entrance','Prescriptions','OTC Medicines','Personal Care','Vitamins','Checkout','Consultation Area'],
  Bookstore:         ['Entrance','Fiction','Non-Fiction',"Children's",'Magazines','Checkout','Reading Area','Display'],
  Bakery:            ['Entrance','Bread','Pastries','Cakes','Display Case','Counter','Seating','Kitchen'],
  Jewelry:           ['Storefront','Window Display','Rings','Necklaces','Bracelets','Watches','Checkout','Safe Area'],
  'Beauty / Salon':  ['Entrance','Reception','Hair Station','Nail Area','Facial Room','Products','Waiting Area'],
  Hardware:          ['Entrance','Tools','Electrical','Plumbing','Paint','Lumber','Garden','Checkout','Storage'],
  Sports:            ['Entrance','Footwear','Apparel','Equipment','Outdoor','Fitness','Sale Area','Checkout'],
  'Furniture Store': ['Entrance','Living Room','Bedroom','Kitchen','Office','Outdoor','Storage','Checkout'],
  'Toy Store':       ['Entrance','Infants','Boys','Girls','Board Games','Outdoor Toys','Display','Checkout'],
  'General Shop':    ['Shop Front','Entry / Door','Main Floor','Shopping Aisle','Product Shelves','Display Area','Cashier Counter','Window Display','Stock Room','Outside'],
}

const SECTION_CATEGORIES_BY_SCAN: Record<string, string[]> = {
  Shop:          SECTION_CATEGORIES_BY_SHOP_TYPE['General Shop'],
  Vehicle:       ['Exterior','Interior','Front','Rear','Side','Engine','Dashboard','Trunk','Wheels','Detail'],
  Product:       ['Front','Back','Side','Top','Bottom','Detail','Packaging','Label'],
  Animal:        ['Full Body','Head','Front','Back','Side','Detail'],
  'House / Room':['Living Room','Bedroom','Kitchen','Bathroom','Entrance','Hallway','Garden','Exterior','Ceiling','Floor'],
  Person:        ['Full Body','Face','Front','Back','Side','Detail'],
  Furniture:     ['Front','Back','Side','Top','Detail','Assembly'],
  Nature:        ['Overview','Close-up','Ground','Sky','Water','Vegetation','Detail'],
  Food:          ['Top','Side','Detail','Plated','Ingredients','Cross-section'],
  'Art / Object':['Front','Back','Side','Top','Detail','Context'],
  Other:         ['General','Front','Back','Side','Top','Detail'],
}

  const toggleAccordion = (key: string) => setAccordionOpen(prev => ({ ...prev, [key]: !prev[key] }))

  // IndexedDB session for crash resilience
  const [scanSessionId, setScanSessionId] = useState<string>('')
  const [pendingSession, setPendingSession] = useState<ScanSession | null>(null)

  // ── Adaptive FPS controls ──
  const [manualFps, setManualFps] = useState(2)
  const [adaptiveEnabled, setAdaptiveEnabled] = useState(true)
  const deviceMotion = useDeviceMotion()

  // ── Resolution tier ──
  const [resolutionTier, setResolutionTier] = useState<ResolutionTier>(() => getScanSettings().resolutionTier)

  const camera = useCamera({
    targetFps: manualFps,
    quality: 0.85,
    maxDimension: 800,
    scanSessionId,
    adaptiveFps: adaptiveEnabled,
    minFps: 1,
    maxFps: 8,
    similarityThreshold: 0.93,
    motionMagnitude: deviceMotion.motionMagnitude,
    resolutionTier,
  })
  const uploader = useUploader()
  const location = useLocation()

  // State for manual location editing
  const [editLat, setEditLat] = useState('')
  const [editLng, setEditLng] = useState('')
  const [manualLocation, setManualLocation] = useState<{ lat: number; lng: number } | null>(null)

  // All pending sessions (for list display on setup step)
  const [allPendingSessions, setAllPendingSessions] = useState<ScanSession[]>([])
  // Frames loaded from IndexedDB for the resume-prompt preview
  const [resumeFrames, setResumeFrames] = useState<PersistedFrame[]>([])
  const [resumePreviewIndex, setResumePreviewIndex] = useState<number | null>(null)

  // ── Re-assign orphaned session state ──
  const [reassigningSessionId, setReassigningSessionId] = useState<string | null>(null)
  const [reassignShopId, setReassignShopId] = useState('')
  const [reassignSectionName, setReassignSectionName] = useState('')
  const [reassignIsNewSection, setReassignIsNewSection] = useState(false)

  // ── Fetch user's shops on mount, then auto-select first if none pre-selected ──
  useEffect(() => {
    fetchShops()
  }, [])

  // ── Auto-init new scan on mount (no URL params) — mirrors CameraPlaceholderScreen ──
  useEffect(() => {
    if (searchParams.get('shopId') || searchParams.get('mode') === 'rescan') return
    setShopId('__new_shop__')
    setNewShopName(autoScanName())
    setNewSectionName('Section 1')
    setSectionIdFromUrl('')
    setSectionName('')
  }, [])

  // Auto-select first shop when shops load and no shopId is set from URL
  // Skip if already in new-scan mode (auto-init above already set it up)
  useEffect(() => {
    if (!shopsLoading && shops.length > 0 && !shopId && !rescanMode) {
      handleShopChange(shops[0].id)
    }
  }, [shopsLoading, shops.length])

  // Sync Details tab fields from selectedShop
  useEffect(() => {
    if (selectedShop) {
      setDetailsName(selectedShop.name || '')
      setDetailsVisibility((selectedShop.visibility as 'public' | 'private') || 'public')
      setDetailsCategory(selectedShop.category || 'Shop')
      setDetailsDescription((selectedShop as any).description || '')
      setDetailsPhone((selectedShop as any).phone || '')
      const hoursRaw = (selectedShop as any).openingHours
      if (hoursRaw) {
        try {
          const h = JSON.parse(hoursRaw)
          setDetailsOpeningHoursEnabled(true)
          if (h.opensAt) setDetailsOpenHour(h.opensAt.slice(0, 5))
          if (h.closesAt) setDetailsCloseHour(h.closesAt.slice(0, 5))
          if (Array.isArray(h.openDays)) setDetailsOpenDays(h.openDays)
        } catch {}
      } else {
        setDetailsOpeningHoursEnabled(false)
        setDetailsOpenDays([0, 1, 2, 3, 4])
      }
      setDetailsSaved(false)
    } else {
      setDetailsName(newShopName)
      setDetailsVisibility('public')
      setDetailsCategory('Shop')
      setDetailsDescription('')
      setDetailsPhone('')
      setDetailsOpeningHoursEnabled(false)
      setDetailsOpenDays([0, 1, 2, 3, 4])
      setDetailsSaved(false)
    }
  }, [shopId, selectedShop?.id])

  useEffect(() => {
    const cats = detailsCategory === 'Shop' && detailsShopType
      ? SECTION_CATEGORIES_BY_SHOP_TYPE[detailsShopType]
      : SECTION_CATEGORIES_BY_SCAN[detailsCategory]
    if (!cats || cats.length === 0) {
      if (sectionCategory) setSectionCategory('')
      return
    }
    if (cats.includes(sectionCategory)) return
    if (cats.includes('Entrance')) {
      setSectionCategory('Entrance')
    } else {
      setSectionCategory('')
    }
  }, [detailsCategory, detailsShopType])

  // ── Capture initial location on mount ──
  useEffect(() => {
    location.captureInitialLocation()
  }, [])

  // ── Check for interrupted uploads on mount ──
  useEffect(() => {
    getPendingSessions().then((sessions) => {
      if (sessions.length > 0) {
        const sorted = sessions.sort((a, b) => b.createdAt - a.createdAt)
        setAllPendingSessions(sorted)
        // Show the most recent pending session
        setPendingSession(sorted[0])
        setStep('resume-prompt')
        // Load frames for preview
        getFramesBySession(sorted[0].id).then((frames) => {
          setResumeFrames(frames.sort((a, b) => a.frameIndex - b.frameIndex))
        }).catch(() => {})
      }
    }).catch((err) => {
      console.warn('[ScanCapturePage] Failed to check pending sessions:', err)
    })
  }, [])

  // Read picked location back from LocationPickerPage — runs whenever URL params change
  useEffect(() => {
    const pickedLat = searchParams.get('pickedLat')
    const pickedLng = searchParams.get('pickedLng')
    const fromSetup = searchParams.get('fromSetup')
    const fromConfirm = searchParams.get('fromConfirm')
    if (pickedLat && pickedLng) {
      const lat = parseFloat(pickedLat)
      const lng = parseFloat(pickedLng)
      if (!isNaN(lat) && !isNaN(lng)) {
        setEditLat(lat.toFixed(6))
        setEditLng(lng.toFixed(6))
        setManualLocation({ lat, lng })
        if (fromSetup === 'true') {
          setStep('setup')
          setActiveTab(0)
        } else if (fromConfirm === 'true') {
          // Return to review after editing location from confirm-location step
          setStep('review')
        } else {
          setStep('confirm-location')
        }
      }
    }
  }, [searchParams])

  // Frame pairs (low-res + HD + optional ultra) for upload
  const framePairs = useMemo(() => camera.frames.map((f) => ({
    blob: f.blob,
    hdBlob: f.hdBlob,
    ...(f.ultraBlob ? { ultraBlob: f.ultraBlob } : {}),
  })), [camera.frames])

  // Start GPS sampling when camera step begins (skip if location was manually set at setup)
  useEffect(() => {
    if (step === 'camera' && camera.isStreaming && !location.isActive && !manualLocation) {
      location.startSampling()
    }
  }, [step, camera.isStreaming, location.isActive, manualLocation])

  // ── Handle shop selection ──
  const handleShopChange = (id: string) => {
    setIsEditingName(false)
    setShopId(id)
    // Reset section when shop changes, then auto-pick first section
    setSectionIdFromUrl('')
    setSectionName('')
    setNewSectionName('')
    if (id === '__new_shop__') {
      // Auto-name new scan as "Scan N"
      const nextNum = shops.length + 1
      setNewShopName(`Scan ${nextNum}`)
      // Auto-name first section
      setNewSectionName('Section 1')
    } else {
      setNewShopName('')
      // Auto-prepare a new section so the scan button is ready immediately
      const shop = shops.find((s) => s.id === id)
      const nextNum = (shop?.sections.length ?? 0) + 1
      setSectionIdFromUrl('')
      setSectionName('')
      setNewSectionName(`Section ${nextNum}`)
    }
  }

  // ── Handle section selection ──
  const handleSectionChange = (sectionId: string) => {
    if (sectionId === '__new__') {
      setSectionIdFromUrl('')
      setSectionName('')
      // Auto-name: "Section N" where N = existing section count + 1
      const nextNum = (selectedShop?.sections.length ?? 0) + 1
      setNewSectionName(`Section ${nextNum}`)
      return
    }
    const section = selectedShop?.sections.find((s) => s.sectionId === sectionId)
    if (section) {
      setSectionIdFromUrl(section.sectionId)
      setSectionName(section.name)
    }
  }

  // ── Setup Step ──
  const handleStartCamera = async () => {
    let resolvedShopId = shopId

    // If creating a new shop inline, do it now
    if (isNewShop) {
      if (!newShopName.trim()) return
      const resolvedCategory = detailsCategory === 'Shop' && detailsShopType
        ? detailsShopType
        : detailsCategory
      const created = await createShop(newShopName.trim(), '', resolvedCategory, null)
      if (!created) {
        const err = useShopStore.getState().error
        if (err) alert(err)
        return
      }
      resolvedShopId = created.id
      setShopId(created.id)
      setNewShopName('')
    }

    // Validate: must have a real shop
    if (!resolvedShopId || resolvedShopId === '__new_shop__') return

    // Resolve section name: from selected section or new section input
    const finalSectionName = sectionName || newSectionName.trim()
    if (!finalSectionName) return

    // If user typed a new section name, save it now
    if (!sectionIdFromUrl && newSectionName.trim()) {
      setSectionName(newSectionName.trim())
    }

    // Create IndexedDB session for crash resilience
    const sessionId = generateSessionId()
    // Always use a random ID so non-ASCII names don't produce empty/duplicate slugs
    const finalSectionId = sectionIdFromUrl || ('sec_' + Math.random().toString(36).slice(2, 10))

    // Check storage availability
    const estimate = await getStorageEstimate()
    if (estimate && estimate.quota - estimate.usage < 200 * 1024 * 1024) {
      const usedMB = Math.round(estimate.usage / 1024 / 1024)
      const quotaMB = Math.round(estimate.quota / 1024 / 1024)
      alert(t('lowStorageWarning', { used: usedMB, quota: quotaMB }))
    }

    await createSession({
      id: sessionId,
      shopId: resolvedShopId,
      sectionId: finalSectionId,
      sectionName: finalSectionName,
      frameCount: 0,
      uploadedCount: 0,
      status: 'capturing',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    })
    setScanSessionId(sessionId)

    // Switch to camera step FIRST so the <video> element mounts,
    // then startCamera will attach the stream via effect.
    setStep('camera')
    camera.startCamera()
  }

  // ── Camera Step ──
  const handleStartRecording = () => {
    camera.startCapture()
  }

  const handleStopRecording = () => {
    camera.stopCapture()
    location.stopSampling() // Stop GPS sampling and calculate average
    if (camera.frames.length > 0) {
      // Update session with final frame count and location
      if (scanSessionId) {
        const loc = manualLocation || location.averageLocation || null
        updateSession(scanSessionId, {
          frameCount: camera.frames.length,
          status: 'ready',
          location: loc,
        }).catch(() => {})
      }
      setStep('review')
    }
  }

  // ── Review Step ──
  const handleRetake = () => {
    camera.clearFrames()
    setStep('camera')
  }

  const handleUpload = async () => {
    setStep('upload')
    camera.stopCamera() // Free camera while uploading
    // Use manual location if edited, otherwise averaged location, otherwise null
    const finalLocation = manualLocation || location.averageLocation
    const finalSectionName = sectionName || newSectionName.trim()

    // Resolve category: when Shop + shopType selected, use shopType as the category value
    const resolvedCategory = detailsCategory === 'Shop' && detailsShopType
      ? detailsShopType
      : (detailsCategory || detailsCustomCategory || undefined)

    // Auto-save all Details tab fields to the shop before uploading
    const targetShopId = shopId !== '__new_shop__' ? shopId : null
    if (targetShopId) {
      const shopUpdates: Parameters<typeof updateShop>[1] = {
        visibility: detailsVisibility,
        ...(resolvedCategory ? { category: resolvedCategory } : {}),
        ...(detailsName.trim() ? { name: detailsName.trim() } : {}),
        ...(detailsDescription.trim() ? { description: detailsDescription.trim() } : {}),
        ...(detailsPhone.trim() ? { phone: detailsPhone.trim() } : {}),
        ...(detailsWhatsapp.trim() ? { whatsapp: detailsWhatsapp.trim() } : {}),
        ...(detailsOpeningHoursEnabled ? { openingHours: JSON.stringify({ opensAt: detailsOpenHour, closesAt: detailsCloseHour, openDays: detailsOpenDays }) } : {}),
      }
      await updateShop(targetShopId, shopUpdates).catch(() => {})
    }

    // Update session location before uploading
    if (scanSessionId) {
      await updateSession(scanSessionId, { location: finalLocation || null }).catch(() => {})
    }

    await uploader.upload(framePairs, shopId, finalSectionName, sectionIdFromUrl || undefined, finalLocation, scanSessionId || undefined, rescanMode ? { mode: 'rescan', anchor_frame_index: anchorFrameIndex ?? undefined } : undefined, resolutionTier === 'ultra', sectionCategory || undefined)
  }

  // ── Upload Step ──
  const handleNewScan = () => {
    // Clean up IndexedDB session if exists
    if (scanSessionId) {
      deleteSessionAndFrames(scanSessionId).catch(() => {})
      setScanSessionId('')
    }
    uploader.reset()
    camera.clearFrames()
    location.clearSamples()
    setManualLocation(null)
    setPendingSession(null)
    setStep('setup')
  }

  // ── Resume handlers ──
  const handleResumeUpload = async () => {
    if (!pendingSession) return
    setStep('upload')
    await uploader.resume(pendingSession)
  }

  const handleRetryUpload = async () => {
    // Retry the current upload: re-read the session from IndexedDB and resume
    if (scanSessionId) {
      const sessions = await getPendingSessions()
      const current = sessions.find((s) => s.id === scanSessionId)
      if (current) {
        uploader.reset()
        setStep('upload')
        await uploader.resume(current)
        return
      }
    }
    // If no active session but there's a pending one, use that
    if (pendingSession) {
      uploader.reset()
      setStep('upload')
      await uploader.resume(pendingSession)
    }
  }

  const handleDiscardPending = async () => {
    if (!pendingSession) return
    await deleteSessionAndFrames(pendingSession.id).catch(() => {})
    // Remove from allPendingSessions
    const remaining = allPendingSessions.filter((s) => s.id !== pendingSession.id)
    setAllPendingSessions(remaining)
    if (remaining.length > 0) {
      setPendingSession(remaining[0])
      getFramesBySession(remaining[0].id).then((frames) => {
        setResumeFrames(frames.sort((a, b) => a.frameIndex - b.frameIndex))
      }).catch(() => {})
    } else {
      setPendingSession(null)
      setResumeFrames([])
      setStep('setup')
    }
  }

  // Re-assign an orphaned session to a different shop
  const handleReassignSave = async (session: ScanSession) => {
    const targetShop = shops.find((s) => s.id === reassignShopId)
    if (!targetShop || !reassignSectionName.trim()) return

    const newSectionId = 'sec_' + reassignSectionName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    await updateSession(session.id, {
      shopId: reassignShopId,
      sectionId: newSectionId,
      sectionName: reassignSectionName.trim(),
    })

    // Update local state so the UI reflects the change immediately
    setAllPendingSessions((prev) =>
      prev.map((s) => s.id === session.id
        ? { ...s, shopId: reassignShopId, sectionId: newSectionId, sectionName: reassignSectionName.trim() }
        : s
      )
    )
    setReassigningSessionId(null)
    setReassignShopId('')
    setReassignSectionName('')
    setReassignIsNewSection(false)

    // Auto-resume the now-valid session
    const updated = { ...session, shopId: reassignShopId, sectionId: newSectionId, sectionName: reassignSectionName.trim() }
    handleSelectPendingSession(updated)
  }

  // Switch to a different pending session
  const handleSelectPendingSession = (session: ScanSession) => {
    setPendingSession(session)
    setResumeFrames([])
    getFramesBySession(session.id).then((frames) => {
      setResumeFrames(frames.sort((a, b) => a.frameIndex - b.frameIndex))
    }).catch(() => {})
    setStep('resume-prompt')
  }

  // ── Location Confirmation handlers ──
  const handleConfirmLocation = () => {
    setStep('review')
  }

  const handleSkipLocation = () => {
    location.clearSamples()
    setManualLocation(null)
    setStep('review')
  }

  const handleSaveEditedLocation = () => {
    const lat = parseFloat(editLat)
    const lng = parseFloat(editLng)
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      setManualLocation({ lat, lng })
      setStep('confirm-location')
    } else {
      alert(t('invalidCoordinates'))
    }
  }

  const handleUseCurrentLocation = () => {
    // Navigate to the map picker from confirm-location step — return to review
    const params = new URLSearchParams({ returnTo: '/scan', fromConfirm: 'true' })
    if (shopId) params.set('shopId', shopId)
    if (sectionName) params.set('sectionName', sectionName)
    if (sectionIdFromUrl) params.set('sectionId', sectionIdFromUrl)
    const loc = manualLocation || location.averageLocation
    if (loc) {
      params.set('lat', loc.lat.toString())
      params.set('lng', loc.lng.toString())
    }
    navigate(`/location-picker?${params.toString()}`)
  }

  const handlePickLocationAtSetup = () => {
    const params = new URLSearchParams({ returnTo: '/scan', fromSetup: 'true' })
    if (shopId) params.set('shopId', shopId)
    if (sectionName) params.set('sectionName', sectionName)
    if (sectionIdFromUrl) params.set('sectionId', sectionIdFromUrl)
    const loc = manualLocation || location.initialLocation
    if (loc) {
      params.set('lat', loc.lat.toString())
      params.set('lng', loc.lng.toString())
    }
    navigate(`/location-picker?${params.toString()}`)
  }

  // ── Inline header name editing ──
  const [isEditingName, setIsEditingName] = useState(false)
  const [headerNameInput, setHeaderNameInput] = useState('')

  const handleStartEditName = () => {
    setHeaderNameInput(selectedShop?.name || newShopName || '')
    setIsEditingName(true)
  }

  const handleSaveHeaderName = async () => {
    const trimmed = headerNameInput.trim()
    if (!trimmed) { setIsEditingName(false); return }
    if (isNewShop) {
      setNewShopName(trimmed)
    } else if (selectedShop) {
      await updateShop(selectedShop.id, { name: trimmed })
    }
    setIsEditingName(false)
  }

  // ── Camera settings panel ──
  const [showCameraSettings, setShowCameraSettings] = useState(false)

  // ── Info toast ──
  const [toastVisible, setToastVisible] = useState(false)
  const [toastOut, setToastOut] = useState(false)

  const dismissToast = () => {
    setToastOut(true)
    setTimeout(() => setToastVisible(false), 300)
  }

  useEffect(() => {
    if (step === 'setup') {
      setToastOut(false)
      setToastVisible(true)
      const timer = setTimeout(() => dismissToast(), 10000)
      return () => clearTimeout(timer)
    } else {
      setToastVisible(false)
    }
  }, [step])

  // ── Tab state for setup step ──
  const [activeTab, setActiveTab] = useState<0 | 1>(0)

  // Auto-select first real shop when switching to Details tab with no real shop selected
  useEffect(() => {
    if (activeTab === 1 && (!shopId || shopId === '__new_shop__') && shops.length > 0) {
      handleShopChange(shops[0].id)
    }
  }, [activeTab])

  // Derived: is scan ready to start?
  const canStartScan =
    (!!shopId && shopId !== '__new_shop__' || (isNewShop && !!newShopName.trim())) &&
    !!(sectionName || newSectionName.trim())

  const handleTabScan = () => {
    if (canStartScan) handleStartCamera()
  }

  const handleDeleteSection = async (sectionId: string, sectionName: string) => {
    if (!selectedShop) return
    if (!window.confirm(`Remove the section "${sectionName}"? If it's already uploaded, it'll be cleared from the cloud too.`)) return
    await deleteSection(selectedShop.id, sectionId)
    if (sectionIdFromUrl === sectionId) {
      setSectionIdFromUrl('')
      setSectionName('')
    }
  }

  const handleDeleteWholeScan = async () => {
    if (!selectedShop) return
    if (!window.confirm(`Delete "${selectedShop.name}" along with all its sections and uploaded files? Heads up — this can't be undone.`)) return
    await deleteShop(selectedShop.id)
    setShopId('')
    setSectionName('')
    setSectionIdFromUrl('')
    setNewSectionName('')
  }

  const handleDetailsSave = async () => {
    if (!selectedShop) return
    try {
      await updateShop(selectedShop.id, {
        name: detailsName || selectedShop.name,
        visibility: detailsVisibility,
        description: detailsDescription || undefined,
        phone: detailsPhone || undefined,
        ...(detailsOpeningHoursEnabled ? { openingHours: JSON.stringify({ opensAt: detailsOpenHour, closesAt: detailsCloseHour, openDays: detailsOpenDays }) } : {}),
      })
      setDetailsSaved(true)
      setTimeout(() => setDetailsSaved(false), 2000)
    } catch (err) {
      console.error('[ScanPage] Failed to save details:', err)
    }
  }

  return (
    <div className={`scan-page${step === 'camera' ? ' scan-page--camera' : ''}`} style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      {step !== 'camera' && <header className="scan-header">
        <div style={{ width: 44 }} />
        {step === 'setup' && isEditingName ? (
          <input
            className="scan-header-name-input"
            value={headerNameInput}
            onChange={(e) => setHeaderNameInput(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveHeaderName() }}
          />
        ) : (
          <h1>
            {step === 'setup'
              ? (selectedShop?.name || (isNewShop && newShopName ? newShopName : null) || t('dukkanScan'))
              : t('dukkanScan')}
          </h1>
        )}
        {step === 'setup' ? (
          isEditingName ? (
            <button className="scan-header-edit-btn" onClick={handleSaveHeaderName}>Save</button>
          ) : (
            <button className="scan-header-edit-btn" onClick={handleStartEditName}>Edit</button>
          )
        ) : (
          <div style={{ width: 44 }} />
        )}
      </header>}

      {/* ── INFO TOAST ── */}
      {toastVisible && step === 'setup' && (
        <div className={`scan-info-toast${toastOut ? ' toast-out' : ''}`}>
          <div className="scan-info-toast-icon">📷</div>
          <div className="scan-info-toast-body">
            <div className="scan-info-toast-heading">
              ✦ You're all set — let's scan!
            </div>
            <div className="scan-info-toast-subheading">
              Just tap ▶ Scan whenever you're ready, or tweak the details below first.
            </div>
            <div className="scan-info-toast-divider" />
            <div className="scan-info-toast-row">
              <span className="scan-info-toast-label">Name</span>
              <span className="scan-info-toast-value">
                {selectedShop?.name || (isNewShop && newShopName) || t('dukkanScan')}
              </span>
            </div>
            <div className="scan-info-toast-row">
              <span className="scan-info-toast-label">Category</span>
              <span className="scan-info-toast-value">
                {detailsCategory === 'Shop' && detailsShopType
                  ? detailsShopType
                  : (detailsCategory || '—')}
              </span>
            </div>
            <div className="scan-info-toast-row">
              <span className="scan-info-toast-label">Visibility</span>
              <span className="scan-info-toast-value">
                {detailsVisibility === 'public' ? '🌐 Public' : '🔒 Private'}
              </span>
            </div>
            <div className="scan-info-toast-hint">
              ✎ Want to add more? Pop over to the Details tab
            </div>
          </div>
          <button className="scan-info-toast-close" onClick={dismissToast}>✕</button>
          <div className="scan-info-toast-progress">
            <div className="scan-info-toast-progress-fill" />
          </div>
        </div>
      )}

      {/* ── RESUME PROMPT ── */}
      {step === 'resume-prompt' && pendingSession && (
        <div className="scan-step scan-resume">
          <div className="scan-resume-card">
            <h2>{t('uploadInterrupted')}</h2>
            <p className="scan-resume-detail">
              <strong>{pendingSession.sectionName}</strong> &middot; {pendingSession.shopId}
            </p>
            <p className="scan-resume-detail">
              {t('framesCaptured', { count: pendingSession.frameCount })}
              {pendingSession.uploadedCount > 0 && (
                <> &middot; {t('alreadyUploaded', { count: pendingSession.uploadedCount })}</>
              )}
            </p>
            {pendingSession.location && (
              <p className="scan-resume-detail">📍 {t('locationSet', 'Location set')}</p>
            )}
            <p className="scan-resume-time">
              {t('startedAt', { time: new Date(pendingSession.createdAt).toLocaleString() })}
            </p>

            {/* ── Frame thumbnail strip ── */}
            {resumeFrames.length > 0 && (
              <div className="scan-resume-frames">
                <p className="scan-resume-frames-label">{t('capturedFrames')}</p>
                <div className="scan-resume-frame-strip">
                  {resumeFrames.slice(0, 20).map((frame, i) => (
                    <img
                      key={frame.id}
                      src={URL.createObjectURL(frame.blob)}
                      alt={t('frameAlt', { number: i + 1 })}
                      className="scan-resume-thumb"
                      onClick={() => setResumePreviewIndex(i)}
                    />
                  ))}
                  {resumeFrames.length > 20 && (
                    <span className="scan-resume-more">+{resumeFrames.length - 20}</span>
                  )}
                </div>
              </div>
            )}

            <div className="scan-resume-actions">
              <button className="scan-btn-primary" onClick={handleResumeUpload}>
                {t('resumeUpload')}
              </button>
              <button className="scan-btn-secondary" onClick={handleDiscardPending}>
                {t('discardAndStartFresh')}
              </button>
            </div>

            {/* Other pending sessions */}
            {allPendingSessions.length > 1 && (
              <div className="scan-resume-others">
                <p className="scan-resume-others-label">{t('otherPendingScans')}</p>
                {allPendingSessions.filter((s) => s.id !== pendingSession.id).map((s) => (
                  <button
                    key={s.id}
                    className="scan-resume-other-item"
                    onClick={() => handleSelectPendingSession(s)}
                  >
                    <strong>{s.sectionName}</strong> &middot; {s.shopId} &middot; {s.frameCount} frames
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Frame Preview Overlay for resume */}
          {resumePreviewIndex !== null && resumePreviewIndex < resumeFrames.length && (
            <FramePreview
              frames={resumeFrames.map((f) => ({ blob: f.blob, index: f.frameIndex }))}
              currentIndex={resumePreviewIndex}
              onNavigate={setResumePreviewIndex}
              onDelete={() => {}} // No deletion in resume mode
              onClose={() => setResumePreviewIndex(null)}
              hideDelete
            />
          )}
        </div>
      )}

      {/* ── SETUP (tabbed layout) ── */}
      {step === 'setup' && (
        <div className="scan-step scan-setup-new">

          {/* Rescan mode banner */}
          {rescanMode && (
            <div className="scan-rescan-banner">
              <span className="scan-rescan-title">🔄 Rescanning: {sectionName || 'Section'}</span>
              <span className="scan-rescan-hint">Point your camera at the spot you'd like to refresh, then hit record.</span>
            </div>
          )}

          {/* ── Tab bar ── */}
          <div className="scan-tabs">
            <button
              className={`scan-tab${activeTab === 0 ? ' active' : ''}`}
              onClick={() => setActiveTab(0)}
            >
              Scans & Sections
            </button>
            <button
              className={`scan-tab${activeTab === 1 ? ' active' : ''}`}
              onClick={() => setActiveTab(1)}
            >
              Details
            </button>
            <div className="scan-tab-indicator" style={{ transform: `translateX(${activeTab * 100}%)` }} />
          </div>

          {/* ── Tab 0: Scans & Sections ── */}
          <div className={`scan-tab-content${activeTab === 0 ? ' active' : ''}`}>

            {/* Scan (shop) slider */}
            <div className="scan-slider-section">
              <div className="scan-slider-label-row">
                <span className="scan-slider-label">SCANS</span>
                {shopId && (
                  <button className="scan-slider-clear" onClick={() => { setShopId(''); setSectionName(''); setSectionIdFromUrl(''); setNewSectionName('') }} title="Clear selection">×</button>
                )}
              </div>
              {shopsLoading ? (
                <div className="scan-slider-loading"><span className="scan-spinner-small" /> {t('common:loading')}</div>
              ) : (
                <div className="scan-slider">
                  {/* New Scan card */}
                  <button
                    className={`scan-slider-card scan-slider-card-new${shopId === '__new_shop__' ? ' active' : ''}`}
                    onClick={() => handleShopChange('__new_shop__')}
                    disabled={rescanMode}
                  >
                    <span className="scan-slider-card-plus">+</span>
                    <span className="scan-slider-card-label">New Scan</span>
                  </button>
                  {/* Existing shops */}
                  {shops.map((s) => (
                    <button
                      key={s.id}
                      className={`scan-slider-card${shopId === s.id ? ' active' : ''}`}
                      onClick={() => handleShopChange(s.id)}
                      disabled={rescanMode}
                    >
                      <div className="scan-slider-card-thumb">
                        <img
                          src={apiUrl(`/api/shops/${s.id}/poster${token ? `?token=${encodeURIComponent(token)}` : ''}`)}
                          alt=""
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />
                        <span className="scan-slider-card-initial">{s.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <span className="scan-slider-card-name">{s.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Section slider — appears when a shop is selected */}
            {(selectedShop || isNewShop) && (
              <div className="scan-slider-section">
                <div className="scan-slider-label-row">
                  <span className="scan-slider-label">SECTIONS</span>
                  {(sectionIdFromUrl || sectionName || newSectionName) && (
                    <button className="scan-slider-clear" onClick={() => { setSectionName(''); setSectionIdFromUrl(''); setNewSectionName('') }} title="Clear section">×</button>
                  )}
                </div>
                {selectedShop && selectedShop.sections.length > 0 ? (
                  <div className="scan-slider scan-slider-sections">
                    {selectedShop.sections.map((sec, i) => (
                      <div key={sec.sectionId} className="scan-slider-card-section-wrap">
                        <button
                          className={`scan-slider-card scan-slider-card-section${sectionIdFromUrl === sec.sectionId ? ' active-section' : ''}`}
                          onClick={() => handleSectionChange(sec.sectionId)}
                        >
                          <div className="scan-slider-section-index">{i + 1}</div>
                          <span className="scan-slider-section-name">{sec.name}</span>
                          <button
                            className="scan-slider-section-delete-icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteSection(sec.sectionId, sec.name) }}
                            title="Delete section"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                            </svg>
                          </button>
                        </button>
                      </div>
                    ))}
                    {/* Add new section card */}
                    <button
                      className={`scan-slider-card scan-slider-card-section scan-slider-card-new${!sectionIdFromUrl ? ' active-section' : ''}`}
                      onClick={() => handleSectionChange('__new__')}
                    >
                      <div className="scan-slider-section-index">+</div>
                      <span className="scan-slider-section-name">New</span>
                    </button>
                  </div>
                ) : null}

                {/* Section name input — always shown when no existing section is selected */}
                {!sectionIdFromUrl && (
                  <input
                    type="text"
                    className="scan-input-text"
                    placeholder={t('sectionNamePlaceholder')}
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                  />
                )}

                {/* Section category chips — adaptive based on scan category / shop type */}
                {(() => {
                  const cats = detailsCategory === 'Shop' && detailsShopType
                    ? SECTION_CATEGORIES_BY_SHOP_TYPE[detailsShopType]
                    : SECTION_CATEGORIES_BY_SCAN[detailsCategory]
                  if (!cats) return null
                  return (
                    <>
                      <label className="scan-slider-label" style={{ marginTop: 12 }}>Section Category</label>
                      <div className="sd-chip-grid" style={{ marginTop: 6 }}>
                        {cats.map((cat) => (
                          <button
                            key={cat}
                            className={`sd-chip${sectionCategory === cat ? ' active' : ''}`}
                            onClick={() => setSectionCategory(sectionCategory === cat ? '' : cat)}
                          >{cat}</button>
                        ))}
                      </div>
                    </>
                  )
                })()}
              </div>
            )}

            {/* Description — shown when a specific section is selected */}
            {sectionIdFromUrl && (
              <div className="scan-slider-section">
                <div className="scan-slider-label-row">
                  <span className="scan-slider-label">SECTION DESCRIPTION</span>
                </div>
                <textarea
                  className="scan-input-text scan-input-textarea"
                  placeholder="Tell us a little about this section…"
                  rows={3}
                  value={detailsDescription}
                  onChange={(e) => setDetailsDescription(e.target.value)}
                />
              </div>
            )}

            {/* Location indicator */}
            <div className="scan-setup-location">
              {manualLocation ? (
                <span className="scan-setup-location-status scan-located">
                  📍 {t('locationSet', 'Location set')}
                  <button className="scan-btn-text scan-btn-locate" onClick={handlePickLocationAtSetup}>{t('change', 'Change')}</button>
                </span>
              ) : location.isLocating ? (
                <span className="scan-setup-location-status scan-locating">
                  <span className="scan-spinner-small" /> {t('locating')}
                  <button className="scan-btn-text scan-btn-locate" onClick={handlePickLocationAtSetup}>{t('setManually', 'Set Manually')}</button>
                </span>
              ) : location.initialLocation ? (
                <span className="scan-setup-location-status scan-located">
                  📍 {t('locationDetected', 'Location detected')}
                  <button className="scan-btn-text scan-btn-locate" onClick={handlePickLocationAtSetup}>{t('change', 'Change')}</button>
                </span>
              ) : (
                <span className="scan-setup-location-status scan-no-location">
                  📍 {t('locationNotDetected')}
                  <button className="scan-btn-text scan-btn-locate" onClick={() => location.captureInitialLocation()}>{t('retry')}</button>
                  <button className="scan-btn-text scan-btn-locate" onClick={handlePickLocationAtSetup}>{t('setManually', 'Set Manually')}</button>
                </span>
              )}
            </div>

            {/* Pending sessions list */}
            {allPendingSessions.length > 0 && (
              <div className="scan-pending-list">
                <h3>{t('pendingScans')}</h3>
                {allPendingSessions.map((s) => {
                  const pendShop = shops.find((sh) => sh.id === s.shopId)
                  return (
                    <div key={s.id} className="scan-pending-item">
                      <div className="scan-pending-item-info">
                        <strong>{s.sectionName}</strong> &middot; {pendShop ? pendShop.name : s.shopId}
                        {!pendShop && <span className="scan-pending-item-warning"> ⚠️ {t('shopNotFound')}</span>}
                        <br />
                        <span className="scan-pending-item-meta">
                          {s.frameCount} frames &middot; {new Date(s.createdAt).toLocaleDateString()}
                          {s.uploadedCount > 0 && <> &middot; {s.uploadedCount} uploaded</>}
                        </span>
                      </div>
                      <div className="scan-pending-item-actions">
                        {pendShop ? (
                          <button className="scan-btn-text" onClick={() => handleSelectPendingSession(s)}>{t('resumeUpload')}</button>
                        ) : (
                          <>
                            <button className="scan-btn-text" onClick={() => {
                              setReassigningSessionId(s.id)
                              setReassignShopId('')
                              setReassignSectionName(s.sectionName || '')
                              setReassignIsNewSection(false)
                            }}>{t('reassign')}</button>
                            <button className="scan-btn-text scan-btn-discard" onClick={async () => {
                              await deleteSessionAndFrames(s.id).catch(() => {})
                              setAllPendingSessions((prev) => prev.filter((p) => p.id !== s.id))
                            }}>{t('discard')}</button>
                          </>
                        )}
                      </div>
                      {reassigningSessionId === s.id && (
                        <div className="scan-reassign-form">
                          <select className="scan-select" value={reassignShopId} onChange={(e) => setReassignShopId(e.target.value)}>
                            <option value="">{t('selectShop')}</option>
                            {shops.map((sh) => <option key={sh.id} value={sh.id}>{sh.name}</option>)}
                          </select>
                          {reassignShopId && (() => {
                            const rShop = shops.find((sh) => sh.id === reassignShopId)
                            return rShop && rShop.sections.length > 0 ? (
                              <select className="scan-select" value={reassignIsNewSection ? '__new__' : reassignSectionName} onChange={(e) => {
                                if (e.target.value === '__new__') { setReassignIsNewSection(true); setReassignSectionName('') }
                                else { setReassignIsNewSection(false); setReassignSectionName(e.target.value) }
                              }}>
                                <option value="">{t('selectSection')}</option>
                                {rShop.sections.map((sec) => <option key={sec.sectionId} value={sec.name}>{sec.name}</option>)}
                                <option value="__new__">+ {t('newSection')}</option>
                              </select>
                            ) : null
                          })()}
                          {reassignShopId && (reassignIsNewSection || !shops.find((sh) => sh.id === reassignShopId)?.sections.length) && (
                            <input type="text" className="scan-input-new-section" placeholder={t('sectionNamePlaceholder')} value={reassignSectionName} onChange={(e) => setReassignSectionName(e.target.value)} />
                          )}
                          <div className="scan-reassign-actions">
                            <button className="scan-btn-primary scan-btn-small" disabled={!reassignShopId || !reassignSectionName.trim()} onClick={() => handleReassignSave(s)}>{t('saveAndResume')}</button>
                            <button className="scan-btn-text" onClick={() => setReassigningSessionId(null)}>{t('cancel')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* ── Tab 1: Details ── */}
          <div className={`scan-tab-content${activeTab === 1 ? ' active' : ''}`}>

            {/* ── Shop selector ── */}
            {shops.length > 0 && (
              <div className="scan-slider-section" style={{ paddingBottom: 14 }}>
                <div className="scan-slider-label-row">
                  <span className="scan-slider-label">Select Scan</span>
                </div>
                <div className="scan-slider">
                  {shops.map((s) => (
                    <button
                      key={s.id}
                      className={`scan-slider-card${shopId === s.id ? ' active' : ''}`}
                      onClick={() => handleShopChange(s.id)}
                    >
                      <div className="scan-slider-card-thumb">
                        <img
                          src={apiUrl(`/api/shops/${s.id}/poster${token ? `?token=${encodeURIComponent(token)}` : ''}`)}
                          alt=""
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />
                        <span className="scan-slider-card-initial">{s.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <span className="scan-slider-card-name">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Basic Info accordion ── */}
            <div className="sd-accordion">
              <button className="sd-accordion-hdr" onClick={() => toggleAccordion('basicInfo')}>
                <span>Basic Info</span>
                <span className="sd-accordion-arrow">{accordionOpen.basicInfo ? '▲' : '▼'}</span>
              </button>
              {accordionOpen.basicInfo && (
                <div className="sd-accordion-body">
                  {/* Scan name */}
                  <label className="sd-field-label">Scan name</label>
                  <div className="sd-input-clear-wrap">
                    <input
                      type="text"
                      className="sd-field-input"
                      placeholder="e.g. My Shop"
                      value={detailsName}
                      onChange={(e) => setDetailsName(e.target.value)}
                    />
                    {detailsName && (
                      <button className="sd-input-clear" onClick={() => setDetailsName('')}>×</button>
                    )}
                  </div>

                  {/* Visibility */}
                  <label className="sd-field-label">Visibility</label>
                  <div className="sd-seg">
                    <button
                      className={`sd-seg-btn${detailsVisibility === 'public' ? ' active' : ''}`}
                      onClick={() => setDetailsVisibility('public')}
                    >Public</button>
                    <button
                      className={`sd-seg-btn${detailsVisibility === 'private' ? ' active' : ''}`}
                      onClick={() => setDetailsVisibility('private')}
                    >Private</button>
                  </div>

                  {/* Category chips */}
                  <label className="sd-field-label">Category</label>
                  <div className="sd-chip-grid">
                    {SCAN_CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        className={`sd-chip${detailsCategory === cat ? ' active' : ''}`}
                        onClick={() => {
                          const next = detailsCategory === cat ? '' : cat
                          setDetailsCategory(next)
                          if (next !== 'Shop') setDetailsShopType('')
                        }}
                      >{cat}</button>
                    ))}
                  </div>

                  {/* Shop Type chips — only when Shop is selected */}
                  {detailsCategory === 'Shop' && (
                    <>
                      <label className="sd-field-label">Shop Type</label>
                      <div className="sd-chip-grid">
                        {SHOP_TYPES.map((type) => (
                          <button
                            key={type}
                            className={`sd-chip${detailsShopType === type ? ' active' : ''}`}
                            onClick={() => setDetailsShopType(detailsShopType === type ? '' : type)}
                          >{type}</button>
                        ))}
                      </div>
                    </>
                  )}
                  <div className="sd-custom-cat-row">
                    <input
                      type="text"
                      className="sd-field-input"
                      placeholder="Add custom category…"
                      value={detailsCustomCategory}
                      onChange={(e) => setDetailsCustomCategory(e.target.value)}
                    />
                    <button
                      className="sd-custom-cat-add"
                      onClick={() => { if (detailsCustomCategory.trim()) { setDetailsCategory(detailsCustomCategory.trim()); setDetailsCustomCategory('') } }}
                    >+ Add</button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Location accordion ── */}
            <div className="sd-accordion">
              <button className="sd-accordion-hdr" onClick={() => toggleAccordion('location')}>
                <span>Location</span>
                <span className="sd-accordion-arrow">{accordionOpen.location ? '▲' : '▼'}</span>
              </button>
              {accordionOpen.location && (
                <div className="sd-accordion-body">
                  {(() => {
                    const loc = manualLocation || location.initialLocation
                    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
                    return loc ? (
                      <>
                        <a
                          href={`https://maps.google.com/?q=${loc.lat},${loc.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="scan-details-location-map-link"
                        >
                          <img
                            src={`https://maps.googleapis.com/maps/api/staticmap?center=${loc.lat},${loc.lng}&zoom=15&size=600x180&scale=2&markers=color:red%7C${loc.lat},${loc.lng}&key=${apiKey}`}
                            alt="Map"
                            className="scan-details-location-map-img"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                        </a>
                        <button className="sd-loc-btn" onClick={handlePickLocationAtSetup}>
                          📍 Use My Current Location
                        </button>
                      </>
                    ) : (
                      <button className="sd-loc-btn" onClick={handlePickLocationAtSetup}>
                        📍 Use My Current Location
                      </button>
                    )
                  })()}
                </div>
              )}
            </div>

            {/* ── Contact accordion ── */}
            <div className="sd-accordion">
              <button className="sd-accordion-hdr" onClick={() => toggleAccordion('contact')}>
                <span>Contact</span>
                <span className="sd-accordion-arrow">{accordionOpen.contact ? '▲' : '▼'}</span>
              </button>
              {accordionOpen.contact && (
                <div className="sd-accordion-body">
                  <label className="sd-field-label">Phone</label>
                  <div className="sd-phone-wrap">
                    <PhoneInput
                      country="jo"
                      value={detailsPhone}
                      onChange={(val) => { setDetailsPhone(val); if (detailsSameWhatsapp) setDetailsWhatsapp(val) }}
                      inputClass="sd-phone-input"
                      buttonClass="sd-phone-flag-btn"
                      containerClass="sd-phone-container"
                      dropdownClass="sd-phone-dropdown"
                      placeholder="Phone number"
                      enableSearch
                      searchClass="sd-phone-search"
                    />
                  </div>
                  <label className="sd-checkbox-row">
                    <input
                      type="checkbox"
                      checked={detailsSameWhatsapp}
                      onChange={(e) => { setDetailsSameWhatsapp(e.target.checked); if (e.target.checked) setDetailsWhatsapp(detailsPhone) }}
                    />
                    <span>Same number for WhatsApp</span>
                  </label>
                  {!detailsSameWhatsapp && (
                    <>
                      <label className="sd-field-label">WhatsApp</label>
                      <div className="sd-phone-wrap">
                        <PhoneInput
                          country="jo"
                          value={detailsWhatsapp}
                          onChange={(val) => setDetailsWhatsapp(val)}
                          inputClass="sd-phone-input"
                          buttonClass="sd-phone-flag-btn"
                          containerClass="sd-phone-container"
                          dropdownClass="sd-phone-dropdown"
                          placeholder="WhatsApp number"
                          enableSearch
                          searchClass="sd-phone-search"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Opening Hours accordion ── */}
            <div className="sd-accordion">
              <button className="sd-accordion-hdr" onClick={() => toggleAccordion('hours')}>
                <span>Opening Hours</span>
                <span className="sd-accordion-arrow">{accordionOpen.hours ? '▲' : '▼'}</span>
              </button>
              {accordionOpen.hours && (
                <div className="sd-accordion-body">
                  <label className="sd-checkbox-row">
                    <input
                      type="checkbox"
                      checked={detailsOpeningHoursEnabled}
                      onChange={(e) => setDetailsOpeningHoursEnabled(e.target.checked)}
                    />
                    <span>Enable opening hours</span>
                  </label>
                  {detailsOpeningHoursEnabled && (
                    <>
                      <div className="sd-hours-row" style={{ marginTop: 12 }}>
                        <div className="sd-hours-field">
                          <label className="sd-field-label">Opens</label>
                          <input type="time" className="sd-field-input" value={detailsOpenHour} onChange={(e) => setDetailsOpenHour(e.target.value)} />
                        </div>
                        <div className="sd-hours-field">
                          <label className="sd-field-label">Closes</label>
                          <input type="time" className="sd-field-input" value={detailsCloseHour} onChange={(e) => setDetailsCloseHour(e.target.value)} />
                        </div>
                      </div>
                      <label className="sd-field-label" style={{ marginTop: 12 }}>Open Days</label>
                      <div className="sd-days-row">
                        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day, idx) => {
                          const active = detailsOpenDays.includes(idx)
                          return (
                            <button
                              key={day}
                              type="button"
                              className={`sd-day-chip${active ? ' active' : ''}`}
                              onClick={() => setDetailsOpenDays(prev =>
                                active ? prev.filter(d => d !== idx) : [...prev, idx]
                              )}
                            >{day}</button>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Scan Quality accordion ── */}
            <div className="sd-accordion">
              <button className="sd-accordion-hdr" onClick={() => toggleAccordion('quality')}>
                <span>Scan Quality</span>
                <span className="sd-accordion-arrow">{accordionOpen.quality ? '▲' : '▼'}</span>
              </button>
              {accordionOpen.quality && (
                <div className="sd-accordion-body">
                  <div className="scan-tier-row">
                    <button
                      type="button"
                      className={`scan-tier-btn${resolutionTier === 'standard' ? ' active' : ''}`}
                      onClick={() => { setResolutionTier('standard'); setScanSettings({ resolutionTier: 'standard' }) }}
                    >
                      <div className="scan-tier-name">Standard (HD)</div>
                      <div className="scan-tier-desc">1080p · Faster upload</div>
                    </button>
                    <button
                      type="button"
                      className={`scan-tier-btn scan-tier-btn-ultra${resolutionTier === 'ultra' ? ' active' : ''}`}
                      onClick={() => { setResolutionTier('ultra'); setScanSettings({ resolutionTier: 'ultra' }) }}
                    >
                      <div className="scan-tier-name">Ultra (4K)</div>
                      <div className="scan-tier-desc">Deep zoom · Larger files</div>
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* ── Floating pill bottom bar ── */}
          <div className="scan-pill-bar">
            {activeTab === 0 ? (
              <div className="scan-pill">
                {allPendingSessions.length > 0 && (
                  <>
                    <button className="scan-pill-btn scan-pill-btn-upload" onClick={() => handleSelectPendingSession(allPendingSessions[0])}>
                      ☁ Upload
                    </button>
                    <div className="scan-pill-divider" />
                  </>
                )}
                <button
                  className={`scan-pill-btn scan-pill-btn-scan${canStartScan ? '' : ' disabled'}`}
                  onClick={handleTabScan}
                  disabled={!canStartScan}
                >
                  {rescanMode ? '↺ Re-scan' : '▶ Scan'}
                </button>
                <div className="scan-pill-divider" />
                <button
                  className="scan-pill-btn scan-pill-btn-add"
                  onClick={() => {
                    setSectionIdFromUrl('')
                    setSectionName('')
                    // Auto-name the new section
                    const nextNum = (selectedShop?.sections.length ?? 0) + 1
                    setNewSectionName(`Section ${nextNum}`)
                  }}
                >
                  + Section
                </button>
              </div>
            ) : (
              <div className="scan-pill">
                <button
                  className="scan-pill-btn scan-pill-btn-scan"
                  onClick={handleDetailsSave}
                  disabled={!selectedShop}
                >
                  💾 Save
                </button>
                {selectedShop && (
                  <>
                    <div className="scan-pill-divider" />
                    <button
                      className="scan-pill-btn scan-pill-btn-delete"
                      onClick={handleDeleteWholeScan}
                    >
                      🗑 Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── CAMERA ── */}
      {step === 'camera' && (
        <div className="scan-step scan-camera">
          <div className="scan-video-container">
            <video ref={camera.videoRef} autoPlay playsInline muted className="scan-video" />
            <canvas ref={camera.canvasRef} style={{ display: 'none' }} />

            {/* Top bar: hint + settings button in one row */}
            <div className="scan-camera-topbar">
              <div className="scan-tip-overlay">
                {t('cameraTip')}
              </div>
              <button
                className={`scan-settings-btn${showCameraSettings ? ' active' : ''}`}
                onClick={() => setShowCameraSettings((s) => !s)}
                aria-label="Camera settings"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </button>
            </div>

            {/* Settings panel */}
            {showCameraSettings && (
              <>
                <div className="scan-settings-backdrop" onClick={() => setShowCameraSettings(false)} />
                <div className="scan-settings-panel">
                  <div className="scan-fps-slider-row">
                    <label className="scan-fps-label">{t('fpsLabel', { fps: manualFps })}</label>
                    <input
                      type="range"
                      min={1}
                      max={8}
                      step={1}
                      value={manualFps}
                      onChange={(e) => setManualFps(Number(e.target.value))}
                      className="scan-fps-slider"
                    />
                    {adaptiveEnabled && camera.isCapturing && (
                      <span className="scan-fps-effective">{t('actualFps', { fps: camera.adaptiveMetrics.currentFps })}</span>
                    )}
                  </div>
                  <div className="scan-fps-toggle-row">
                    <label className="scan-fps-toggle">
                      <input
                        type="checkbox"
                        checked={adaptiveEnabled}
                        onChange={(e) => setAdaptiveEnabled(e.target.checked)}
                      />
                      <span>{t('adaptive')}</span>
                    </label>
                    {adaptiveEnabled && !deviceMotion.isAvailable && (
                      <button
                        className="scan-btn-text scan-btn-motion-perm"
                        onClick={() => deviceMotion.requestPermission()}
                      >
                        {t('enableMotionSensor')}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {camera.isCapturing && (
              <div className="scan-recording-badge">
                <span className="scan-dot" /> {t('rec')} &middot; {t('framesCount', { count: camera.frames.length })}
                {adaptiveEnabled && camera.adaptiveMetrics.framesSkipped > 0 && (
                  <span className="scan-skipped-badge"> &middot; {t('framesSkipped', { count: camera.adaptiveMetrics.framesSkipped })}</span>
                )}
                {adaptiveEnabled && camera.adaptiveMetrics.blurSkipped > 0 && (
                  <span className="scan-blur-badge"> &middot; {t('framesBlurry', { count: camera.adaptiveMetrics.blurSkipped })}</span>
                )}
                {scanSessionId && <span className="scan-saved-badge"> &middot; {t('saved')}</span>}
              </div>
            )}
            {adaptiveEnabled && camera.isCapturing && camera.adaptiveMetrics.showSlowDownWarning && (
              <div className="scan-slow-down-overlay">
                <span className="scan-slow-down-icon">🐢</span>
                <span className="scan-slow-down-text">{t('slowDownWarning')}</span>
              </div>
            )}
          </div>

          {camera.error && <div className="scan-error scan-error--floating">{camera.error}</div>}

          <div className="scan-camera-ui">
            <div className="scan-controls">
              {!camera.isCapturing ? (
                <button className="scan-btn-record" onClick={handleStartRecording} disabled={!camera.isStreaming}>
                  <span className="scan-record-circle" />
                  {t('startRecording')}
                </button>
              ) : (
                <button className="scan-btn-stop" onClick={handleStopRecording}>
                  <span className="scan-stop-square" />
                  {t('stopCount', { count: camera.frames.length })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRM LOCATION ── */}
      {step === 'confirm-location' && (
        <div className="scan-step scan-location-confirm">
          <h2>{t('captureComplete')}</h2>
          
          {location.averageLocation || manualLocation ? (
            <div className="scan-location-info">
              <h3>{t('locationCaptured')}</h3>
              {manualLocation ? (
                <p className="scan-location-meta">📍 {t('manuallyEntered')}</p>
              ) : location.averageLocation ? (
                <p className="scan-location-meta">
                  📍 {t('locationAccuracy', { samples: location.averageLocation.sampleCount, accuracy: Math.round(location.averageLocation.accuracy) })}
                </p>
              ) : null}
              <a
                href={`https://maps.google.com/?q=${(manualLocation || location.averageLocation)!.lat},${(manualLocation || location.averageLocation)!.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="scan-location-map-link"
              >
                🗺 {t('viewOnGoogleMaps')}
              </a>
            </div>
          ) : (
            <div className="scan-location-info">
              <p className="scan-location-warning">{t('locationNotAvailable')}</p>
              <p className="scan-location-meta">
                {t('locationUnavailableHint')}
              </p>
            </div>
          )}

          <p className="scan-location-prompt">{t('isThisCorrect')}</p>

          <div className="scan-location-actions">
            <button className="scan-btn-primary" onClick={handleConfirmLocation}>
              ✓ {t('common:continue')}
            </button>
            <button className="scan-btn-secondary" onClick={handleUseCurrentLocation}>
              🗺 {t('changeOnMap', 'Change on Map')}
            </button>
            <button className="scan-btn-text" onClick={handleSkipLocation}>
              {t('skipLocation')}
            </button>
          </div>
        </div>
      )}

      {/* ── EDIT LOCATION ── */}
      {step === 'edit-location' && (
        <div className="scan-step scan-location-edit">
          <h2>{t('editLocation')}</h2>
          
          <div className="scan-location-form">
            <div className="scan-field">
              <label>{t('latitude')}</label>
              <input
                type="number"
                step="0.0001"
                placeholder={t('latitudePlaceholder')}
                value={editLat}
                onChange={(e) => setEditLat(e.target.value)}
                className="scan-location-input"
              />
            </div>
            
            <div className="scan-field">
              <label>{t('longitude')}</label>
              <input
                type="number"
                step="0.0001"
                placeholder={t('longitudePlaceholder')}
                value={editLng}
                onChange={(e) => setEditLng(e.target.value)}
                className="scan-location-input"
              />
            </div>

            <button className="scan-btn-secondary scan-btn-gps" onClick={handleUseCurrentLocation}>
              {t('useMyCurrentLocation')}
            </button>

            <div className="scan-location-edit-actions">
              <button className="scan-btn-primary" onClick={handleSaveEditedLocation}>
                {t('common:save')}
              </button>
              <button className="scan-btn-text" onClick={() => setStep('confirm-location')}>
                {t('common:cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REVIEW ── */}
      {step === 'review' && (
        <div className="scan-step scan-review" style={{ background: 'var(--bg-primary)' }}>
          <div className="scan-summary">
            <h2>{t('framesCapturedTitle', { count: camera.frames.length })}</h2>
            <p>{sectionName || newSectionName} &middot; {selectedShop?.name || shopId}</p>
            <p className="scan-tap-hint">{t('tapToPreview')}</p>
          </div>

          <div className="scan-frame-grid">
            {camera.frames.map((frame, i) => (
              <img
                key={frame.index}
                src={URL.createObjectURL(frame.blob)}
                alt={t('frameAlt', { number: i + 1 })}
                className="scan-grid-thumb"
                onClick={() => setPreviewIndex(i)}
              />
            ))}
          </div>

          <div className="scan-review-actions scan-review-actions-sticky">
            <button className="scan-btn-secondary" onClick={handleRetake}>
              {t('retake')}
            </button>
            <button className="scan-btn-primary" onClick={handleUpload} disabled={camera.frames.length === 0}>
              {t('uploadFrames', { count: camera.frames.length })}
            </button>
          </div>

          {/* ── Frame Preview Overlay ── */}
          {previewIndex !== null && previewIndex < camera.frames.length && (
            <FramePreview
              frames={camera.frames}
              currentIndex={previewIndex}
              onNavigate={setPreviewIndex}
              onDelete={(idx) => {
                camera.removeFrame(idx)
                if (camera.frames.length <= 1) {
                  setPreviewIndex(null)
                } else if (idx >= camera.frames.length - 1) {
                  setPreviewIndex(idx - 1)
                }
              }}
              onClose={() => setPreviewIndex(null)}
            />
          )}
        </div>
      )}

      {/* ── UPLOAD / PROGRESS ── */}
      {step === 'upload' && (
        <div className="scan-step scan-upload" style={{ background: 'var(--bg-primary)' }}>
          <StatusView
            phase={uploader.phase}
            progress={uploader.progress}
            error={uploader.error}
            onRetry={handleRetryUpload}
            onNewScan={handleNewScan}
          />
        </div>
      )}
    </div>
  )
}

// ── Frame Preview with swipe ──
function FramePreview({
  frames,
  currentIndex,
  onNavigate,
  onDelete,
  onClose,
  hideDelete = false,
}: {
  frames: { blob: Blob; index: number }[]
  currentIndex: number
  onNavigate: (idx: number) => void
  onDelete: (idx: number) => void
  onClose: () => void
  hideDelete?: boolean
}) {
  const { t } = useTranslation('scan')
  const touchStartX = useRef(0)
  const touchDeltaX = useRef(0)
  const stripRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current
    // Apply live translate for feedback
    if (stripRef.current) {
      stripRef.current.style.transform = `translateX(${touchDeltaX.current}px)`
      stripRef.current.style.transition = 'none'
    }
  }, [])

  const handleTouchEnd = useCallback(() => {
    const delta = touchDeltaX.current
    const threshold = 50 // minimum px to count as swipe
    if (stripRef.current) {
      stripRef.current.style.transform = ''
      stripRef.current.style.transition = 'transform 0.2s ease'
    }
    if (delta < -threshold && currentIndex < frames.length - 1) {
      onNavigate(currentIndex + 1) // swipe left → next
    } else if (delta > threshold && currentIndex > 0) {
      onNavigate(currentIndex - 1) // swipe right → prev
    }
    touchDeltaX.current = 0
  }, [currentIndex, frames.length, onNavigate])

  return (
    <div className="scan-preview-overlay" onClick={onClose}>
      <div className="scan-preview-content" onClick={(e) => e.stopPropagation()}>
        <div className="scan-preview-header">
          <span className="scan-preview-counter">
            {t('frameCounter', { current: currentIndex + 1, total: frames.length })}
          </span>
          <button className="scan-preview-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div
          className="scan-preview-image-wrap"
          ref={stripRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {currentIndex > 0 && (
            <button className="scan-preview-nav scan-preview-prev" onClick={() => onNavigate(currentIndex - 1)}>
              ‹
            </button>
          )}
          <img
            src={URL.createObjectURL(frames[currentIndex].blob)}
            alt={t('frameAlt', { number: currentIndex + 1 })}
            className="scan-preview-img"
            draggable={false}
          />
          {currentIndex < frames.length - 1 && (
            <button className="scan-preview-nav scan-preview-next" onClick={() => onNavigate(currentIndex + 1)}>
              ›
            </button>
          )}
        </div>

        {!hideDelete && (
          <div className="scan-preview-actions">
            <button className="scan-btn-delete" onClick={() => onDelete(currentIndex)}>
              {t('deleteFrame')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Status sub-component ──
function StatusView({
  phase,
  progress,
  error,
  onRetry,
  onNewScan,
}: {
  phase: string
  progress: { total: number; uploaded: number; failed: number; percent: number }
  error: string | null
  onRetry?: () => void
  onNewScan?: () => void
}) {
  const { t } = useTranslation('scan')

  if (error) {
    return (
      <div className="scan-status scan-status-error">
        <div className="scan-status-icon">✗</div>
        <h2>{t('common:error')}</h2>
        <p>{error}</p>
        <div className="scan-upload-error-actions">
          {onRetry && (
            <button className="scan-btn-primary" onClick={onRetry} style={{ marginTop: 24 }}>
              {t('retryUpload')}
            </button>
          )}
          {onNewScan && (
            <button className="scan-btn-secondary" onClick={onNewScan} style={{ marginTop: 8 }}>
              {t('newScan')}
            </button>
          )}
        </div>
      </div>
    )
  }

  if (phase === 'creating') {
    return (
      <div className="scan-status">
        <div className="scan-spinner" />
        <h2>{t('creatingScanSession')}</h2>
      </div>
    )
  }

  if (phase === 'uploading') {
    return (
      <div className="scan-status">
        <div className="scan-progress-bar">
          <div className="scan-progress-fill" style={{ width: `${progress.percent}%` }} />
        </div>
        <h2>{t('uploadingFrames')}</h2>
        <p>
          {t('uploadProgress', { uploaded: progress.uploaded, total: progress.total })}
          {progress.failed > 0 && <span className="scan-failed"> ({t('uploadFailedCount', { count: progress.failed })})</span>}
        </p>
      </div>
    )
  }

  // Reaching 'done' now means the frames are in Azure -- nothing downstream is waited on, so
  // there is no scan.status to consult and no 3D to link to yet.
  if (phase === 'done') {
    return <ScanCompleteView onNewScan={onNewScan} t={t} />
  }

  return null
}

function ScanCompleteView({
  onNewScan,
  t,
}: {
  onNewScan?: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <div className="sc-wrap">
      {/* Ambient background glow */}
      <div className="sc-glow" />

      {/* Sonar rings */}
      <div className="sc-rings">
        {[0, 1, 2].map((i) => (
          <div key={i} className="sc-ring" style={{ '--sc-i': i } as React.CSSProperties} />
        ))}
      </div>

      {/* Burst particles */}
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="sc-particle"
          style={{ '--sc-angle': `${i * 30}deg` } as React.CSSProperties}
        />
      ))}

      {/* Central icon */}
      <div className="sc-icon">
        <svg className="sc-checkmark" viewBox="0 0 60 60" fill="none">
          <polyline
            className="sc-check-path"
            points="14,31 24,42 46,18"
            stroke="white"
            strokeWidth="4.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Text */}
      <div className="sc-text">
        <span className="sc-badge">✦ COMPLETE ✦</span>
        <h1 className="sc-title">{t('scanComplete')}</h1>
        <p className="sc-subtitle">Frames uploaded. Your 3D shop is being built — it will appear here shortly.</p>
      </div>

      {/* Actions */}
      <div className="sc-actions">
        {onNewScan && (
          <button className="sc-btn-ghost" onClick={onNewScan}>
            {t('newScan')}
          </button>
        )}
      </div>
    </div>
  )
}
